/**
 * Device continuity boot.
 *
 * Runs once, during browser idle time, and does exactly three things:
 *   - keeps one anonymous visitor id across cookie / storage wipes,
 *   - restores a previously saved cart when the local cart is empty,
 *   - records a light "this device is active" intent signal.
 *
 * Fails silently and completely: if the device cannot be recognised, the
 * shop behaves exactly as before.
 */
import { useEffect } from "react";
import { useCartStore } from "@/stores/cartStore";

export function DeviceContinuity() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    let cancelled = false;

    void (async () => {
      try {
        const { getDeviceIdWhenIdle } = await import("@/lib/identity/deviceFingerprint");
        const deviceId = await getDeviceIdWhenIdle();
        if (!deviceId || cancelled) return;

        const { getAnonymousId, setAnonymousId } = await import("@/lib/identity/anonymousId");
        const localAnonId = getAnonymousId();
        if (!localAnonId) return;

        const { resolveDeviceIdentity, restoreDeviceCart } = await import(
          "@/lib/device-identity.functions"
        );

        const identity = await resolveDeviceIdentity({
          data: { deviceId, anonymousId: localAnonId },
        });
        if (cancelled) return;

        // Returning device whose local id was wiped — adopt the original id
        // so past intent, carts and leads stay on one visitor.
        if (identity.ok && identity.anonymousId && identity.anonymousId !== localAnonId) {
          setAnonymousId(identity.anonymousId);
        }

        // Share the remembered product interest with the WhatsApp button.
        if (identity.ok && identity.primaryInterest) {
          try {
            sessionStorage.setItem("tw_primary_interest", identity.primaryInterest);
          } catch {
            /* private mode */
          }
          window.dispatchEvent(new CustomEvent("tw:primary-interest", { detail: identity.primaryInterest }));
        }

        // Third visit or later → offer the returning-visitor first-order reward.
        if (identity.ok && (identity.seenCount ?? 1) >= 3) {
          const detail = { seenCount: identity.seenCount, primaryInterest: identity.primaryInterest ?? null };
          try {
            sessionStorage.setItem("tw_returning_visitor", JSON.stringify(detail));
          } catch {
            /* private mode */
          }
          window.dispatchEvent(new CustomEvent("tw:returning-visitor", { detail }));
        }


        // Restore a saved cart only when there is nothing in the local cart.
        if (useCartStore.getState().items.length > 0) return;
        const saved = await restoreDeviceCart({ data: { deviceId } });
        if (cancelled || !saved.ok || saved.items.length === 0) return;

        useCartStore.getState().hydrateItems(
          saved.items.map((i) => ({
            lineId: null,
            variantId: i.variantId,
            productHandle: i.productHandle,
            productTitle: i.productTitle,
            imageUrl: i.imageUrl ?? null,
            price: { amount: i.priceAmount, currencyCode: i.currencyCode },
            quantity: i.quantity,
          })),
        );
      } catch {
        /* continuity is best-effort and never blocks the shop */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}

export default DeviceContinuity;
